const roadmapPage = {
  toc: {
    today: "Where we are today",
    planned: "Planned releases",
    influence: "Write to us",
  },
  header: {
    epigraph: ["Know from where you came,", "and to where you are going."],
    kicker: "Roadmap",
    title: "What comes next",
  },
  sections: {
    today: {
      title: "Where we are today",
      body: "The system does what it exists to do: data can be uploaded, personal data is found and replaced with pseudonyms, researchers can request and receive data, and every step is logged.",
      badge: "Built",
      intro: "Working today, in short:",
      items: {
        pseudonymization: {
          title: "Pseudonymization",
          body: "each project has its own key, so pseudonyms in one project cannot be matched with another. Original values are kept encrypted, so authorized re-identification stays possible and logged.",
        },
        accounts: {
          title: "Accounts and access",
          body: "invitation-only accounts, mandatory two-factor login, and roles for researchers, project leads, managers, and data providers.",
        },
        catalogue: {
          title: "Dataset catalogue",
          body: "column descriptions and summary statistics, separate control over who sees a dataset and who may download it, and bulk download with a ready-to-run notebook and R script.",
        },
        requests: {
          title: "Data requests",
          body: "researchers pick the columns they need, the project lead reviews, and requests can be passed on to external data providers.",
        },
        screening: {
          title: "Upload screening",
          body: "an automatic scan finds likely personal data and pre-selects it, the uploader can mark other columns by hand, and a person confirms before anything is stored.",
        },
        expiry: {
          title: "Expiry that deletes",
          body: "a dataset can be given an expiry date at upload or later. When it passes, the data and its key are erased and the erasure is recorded, so retention promises in an ethics approval are kept by the system.",
        },
        archiving: {
          title: "Archived projects",
          body: "a finished project can be archived, which freezes it until it is restored.",
        },
        notifications: {
          title: "Notifications",
          body: "in-app notices about requests, uploads, and invitations, plus announcements from project leads and managers.",
        },
      },
    },
    planned: {
      title: "Planned releases",
      intro: "Releases are listed in the order we plan to build them. Dates are approximate and may shift.",
      partial: "partly built",
      releases: {
        v1_0: {
          badge: "Approx. Q4 2026",
          title: "Production release",
          summary: "The remaining must-haves and the security audit.",
        },
        v1_1: {
          badge: "Approx. Q1 2027",
          title: "Audit, access and integrations",
          summary: "Visibility into usage, finer control over access, formal re-identification, and REDCap.",
        },
        v1_2: {
          badge: "Approx. Q2 2027",
          title: "Export and convenience",
          summary: "Better export options and starter packages.",
        },
        later: {
          badge: "No date yet",
          title: "Not yet scheduled",
          summary: "Larger features, or ones that depend on other systems.",
        },
      },
      items: {
        sharingMode: {
          title: "One project key, or a key per researcher",
          body: "today everyone in a project shares one key, so the same person has the same pseudonym for all and researchers can combine each other's data. A project setting will let each researcher have their own key instead; they then cannot link their data with each other's.",
        },
        directShare: {
          title: "Share data without a request",
          body: "a project lead can give data directly to all members or to selected individuals. Today this works only for the whole project, as a default at upload.",
        },
        removeFiles: {
          title: "Remove files from a project",
          body: "remove one or more files from a project.",
        },
        multiFileRequest: {
          title: "Request several files at once",
          body: "ask for access to more than one file in a single request.",
        },
        excelDownload: {
          title: "Excel download",
          body: "download data in Excel format.",
        },
        securityAudit: {
          title: "Security audit",
          body: "an independent audit before the system is declared production-ready.",
        },
        auditHistory: {
          title: "Usage history",
          body: "see your own history, the full history of a project, and for system administrators the whole system. The data is already recorded.",
        },
        reidentification: {
          title: "Re-identification requests",
          body: "request and approve re-identification of a list of pseudonyms inside the system, done securely and requiring approval by more than one person. The exact design is not yet decided. It is possible today by emailing help@irei.is, where a system administrator does it and every use is logged.",
        },
        editAfterUpload: {
          title: "Edit access after upload",
          body: "change who can see and access a dataset after it is uploaded. Expiry can already be edited.",
        },
        promoteToPi: {
          title: "Promote a member to project lead",
          body: "promote an existing member from the project's member list. The role can be given today when adding a member.",
        },
        shareEndDate: {
          title: "End date for sharing",
          body: "access ends automatically on a chosen date.",
        },
        redcap: {
          title: "REDCap integration",
          body: "fetch and pseudonymize data from a REDCap project.",
        },
        redcapLinks: {
          title: "Linked REDCap projects",
          body: "see which REDCap projects are linked to your project.",
        },
        passThrough: {
          title: "Upload files without pseudonymization",
          body: "attach supporting files that do not need pseudonymizing.",
        },
        sasExport: {
          title: "SAS export",
          body: "download data in SAS format.",
        },
        batchUpload: {
          title: "Batch upload",
          body: "upload and pseudonymize several files at once with the same settings.",
        },
        starterPackage: {
          title: "Python/R starter package",
          body: "downloads already include a notebook and an R script that load the data. Next: a data dictionary and environment files so analyses run the same on any computer.",
        },
        nextcloud: {
          title: "Send to Nextcloud",
          body: "send data to your own Nextcloud storage.",
        },
        crossProject: {
          title: "Cross-project pseudonym matching",
          body: "let pseudonyms be matched across linked projects. Complex, needs careful planning.",
        },
        lockProject: {
          title: "Locked projects",
          body: "lock a project so only a system administrator can reopen it.",
        },
        jupyterhub: {
          title: "Send to JupyterHub",
          body: "deliver data straight into a secure JupyterHub workspace, so it never has to be downloaded to a personal computer. A high priority, waiting on JupyterHub being production-ready.",
        },
        remoteWorkspace: {
          title: "Secure remote workspace",
          body: "if a notebook is not enough, a full desktop in the browser (for example KASM Workspaces) for tools such as RStudio, SAS or Stata, with no way to copy data out. Only if a project needs it.",
        },
      },
    },
    influence: {
      title: "Write to us",
      intro: {
        before:
          "The plan is flexible, and priorities come from the people using the system. If something is missing for your work, tell us. No form or ticket system: a plain email to",
        after: "reaches the people who build this.",
      },
      writeToUsBox: {
        kicker: "Write to us at",
        welcomeIntro: "Any of these is welcome:",
        items: {
          suggestions: {
            title: "Suggestions and feature requests",
            body: "what would make the system fit the way your group actually works.",
          },
          bugReports: {
            title: "Bug reports",
            body: "what you did, what you expected, and what happened instead. A screenshot and the rough time help us find it in the logs.",
          },
          newDepartment: {
            title: "Requests for a new department",
            body: "this is addressed to the manager of a department, institute, or research unit that would like its own separate space in the system. You are also welcome to write to us to arrange a meeting, a demo, and an overview of the system.",
          },
          questions: {
            title: "Questions before you commit data",
            body: "about the security model, what the system does not yet do, or what we could commit to for your ethics application.",
          },
        },
      },
      contactNote:
        "Your project's principal investigator and your department's manager can also bring things to us, and remain the fastest route for anything about your own account or access.",
      seeAlso: {
        before: "For how the system works day to day, see",
        aboutLink: "About",
        middle: "; for how it is protected, see",
        securityLink: "Security",
      },
    },
  },
};

export default roadmapPage;
